import React from 'react';
import SETTING_KEYS from '../../constants/setting-keys';

type TFunc = (key: string, opts?: Record<string, any>) => string;

const hasValue = (value: any): boolean =>
    value !== undefined && value !== null && value !== '';

interface SubField {
    label: string;
    value: string;
}

interface StructuredEntry {
    fields: SubField[];
}

function SubFieldCard({ entry, index }: { entry: StructuredEntry; index: number }) {
    return (
        <div className="flex flex-wrap gap-x-6 gap-y-1.5 rounded border border-[#e6e6e4] px-3 py-2">
            {entry.fields.map((f) => (
                <div key={`${index}-${f.label}`} className="min-w-[100px]">
                    <div className="text-[9px] font-medium uppercase tracking-[0.12em] text-[#999]">
                        {f.label}
                    </div>
                    <div className="text-[12px] leading-snug">{f.value}</div>
                </div>
            ))}
        </div>
    );
}

function spatialGradientEntries(conditionals: any, t: TFunc): StructuredEntry[] | null {
    const sg = conditionals?.[SETTING_KEYS.spatialGradient];
    const gradients = Array.isArray(sg?.gradients)
        ? sg.gradients.filter(
              (g: any) => hasValue(g?.value) || hasValue(g?.condition)
          )
        : [];
    if (!gradients.length) return null;

    const lNote = t('conditionals.preview.note');
    const lMaxGrad = t('conditionals.preview.maxGradient');
    const lCond = t('conditionals.preview.condition');

    const entries: StructuredEntry[] = [];
    if (typeof sg.text === 'string' && sg.text.trim()) {
        entries.push({ fields: [{ label: lNote, value: sg.text.trim() }] });
    }
    for (const gradient of gradients) {
        const fields: SubField[] = [];
        if (hasValue(gradient.value)) {
            const v = Number(gradient.value);
            fields.push({
                label: lMaxGrad,
                value: Number.isFinite(v)
                    ? `≤ ${v} Gauss/cm (${v / 100} T/m)`
                    : String(gradient.value),
            });
        }
        if (hasValue(gradient.condition))
            fields.push({ label: lCond, value: gradient.condition });
        if (fields.length) entries.push({ fields });
    }
    return entries.length ? entries : null;
}

function sarEntries(conditionals: any, t: TFunc): StructuredEntry[] | null {
    const entries = conditionals?.[SETTING_KEYS.sar];
    if (!Array.isArray(entries)) return null;
    const valid = entries.filter(
        (e: any) =>
            hasValue(e?.value) ||
            hasValue(e?.condition) ||
            hasValue(e?.operatingMode)
    );
    if (!valid.length) return null;

    const lSar = t('conditionals.preview.sarLimit');
    const lMode = t('conditionals.preview.operatingMode');
    const lCond = t('conditionals.preview.condition');
    const lScan = t('conditionals.preview.scanTime');

    return valid.map((e: any) => {
        const fields: SubField[] = [];
        if (hasValue(e.value)) fields.push({ label: lSar, value: `${e.value} W/kg` });
        if (hasValue(e.operatingMode)) fields.push({ label: lMode, value: e.operatingMode });
        if (hasValue(e.condition)) fields.push({ label: lCond, value: e.condition });
        if (hasValue(e.scanningTime)) fields.push({ label: lScan, value: `${e.scanningTime} min` });
        return { fields };
    });
}

function temperatureEntries(conditionals: any, t: TFunc): StructuredEntry[] | null {
    const entries = conditionals?.[SETTING_KEYS.temp];
    if (!Array.isArray(entries)) return null;
    const valid = entries.filter(
        (e: any) => hasValue(e?.maxRise) || hasValue(e?.condition)
    );
    if (!valid.length) return null;

    const lRise = t('conditionals.preview.maxRise');
    const lCond = t('conditionals.preview.condition');

    return valid.map((e: any) => {
        const fields: SubField[] = [];
        if (hasValue(e.maxRise)) fields.push({ label: lRise, value: `≤ ${e.maxRise}°C` });
        if (hasValue(e.condition)) fields.push({ label: lCond, value: e.condition });
        return { fields };
    });
}

function artefactEntries(conditionals: any, t: TFunc): StructuredEntry[] | null {
    const entries = conditionals?.[SETTING_KEYS.artefacts];
    if (!Array.isArray(entries)) return null;
    const valid = entries.filter(
        (e: any) =>
            hasValue(e?.distance) ||
            hasValue(e?.condition) ||
            hasValue(e?.pulseSequence) ||
            hasValue(e?.mrSystem)
    );
    if (!valid.length) return null;

    const lDist = t('conditionals.preview.distance');
    const lPulse = t('conditionals.preview.pulseSequence');
    const lMr = t('conditionals.preview.mrSystem');
    const lCond = t('conditionals.preview.condition');

    return valid.map((e: any) => {
        const fields: SubField[] = [];
        if (hasValue(e.distance)) fields.push({ label: lDist, value: `${e.distance} mm` });
        if (hasValue(e.pulseSequence)) fields.push({ label: lPulse, value: e.pulseSequence });
        if (hasValue(e.mrSystem)) fields.push({ label: lMr, value: `${e.mrSystem} T` });
        if (hasValue(e.condition)) fields.push({ label: lCond, value: e.condition });
        return { fields };
    });
}

const STRUCTURED_BUILDERS: Record<
    string,
    (conditionals: any, t: TFunc) => StructuredEntry[] | null
> = {
    'Spatial Gradient': spatialGradientEntries,
    SAR: sarEntries,
    'Temperature Rise': temperatureEntries,
    Artefacts: artefactEntries,
};

export function renderConditionalsListValue(
    label: string,
    conditionals: any,
    t: TFunc
): React.ReactNode | null {
    if (label === 'Static Magnetic Field') {
        const values = conditionals?.[SETTING_KEYS.fieldStrength];
        const valid = Array.isArray(values) ? values.filter(hasValue) : [];
        if (!valid.length) return null;
        const sorted = [...valid].sort((a, b) => Number(a) - Number(b));
        return (
            <div className="flex flex-wrap gap-2">
                {sorted.map((value, index) => (
                    <span
                        key={index}
                        className="whitespace-nowrap rounded border border-spot px-3 py-0.5 text-[12px] font-medium text-spot"
                    >
                        {value} T
                    </span>
                ))}
            </div>
        );
    }

    const builder = STRUCTURED_BUILDERS[label];
    if (!builder || !conditionals) return null;
    const entries = builder(conditionals, t);
    if (!entries) return null;

    return (
        <div className="space-y-2">
            {entries.map((entry, index) => (
                <SubFieldCard key={index} entry={entry} index={index} />
            ))}
        </div>
    );
}

// Plain-text rendering for copy mode.

interface EntryLine {
    primary: string;
    secondary?: string;
}

function spatialGradientLines(conditionals: any): EntryLine[] | null {
    const sg = conditionals?.[SETTING_KEYS.spatialGradient];
    const gradients = Array.isArray(sg?.gradients)
        ? sg.gradients.filter(
              (g: any) => hasValue(g?.value) || hasValue(g?.condition)
          )
        : [];
    if (!gradients.length) return null;

    const lines: EntryLine[] = [];
    if (typeof sg.text === 'string' && sg.text.trim()) {
        lines.push({ primary: sg.text.trim() });
    }
    for (const gradient of gradients) {
        const value = Number(gradient.value);
        const amount = Number.isFinite(value)
            ? `≤ ${value} Gauss/cm (${value / 100} T/m)`
            : String(gradient.value ?? '');
        lines.push({
            primary: [amount, gradient.condition]
                .filter(Boolean)
                .join(' — '),
        });
    }
    return lines;
}

function sarLines(conditionals: any): EntryLine[] | null {
    const entries = conditionals?.[SETTING_KEYS.sar];
    if (!Array.isArray(entries)) return null;
    const valid = entries.filter(
        (entry: any) =>
            hasValue(entry?.value) ||
            hasValue(entry?.condition) ||
            hasValue(entry?.operatingMode)
    );
    if (!valid.length) return null;

    return valid.map((entry: any) => {
        const amount = hasValue(entry.value) ? `${entry.value} W/kg` : '';
        const secondaryParts = [
            hasValue(entry.operatingMode)
                ? `Operating mode: ${entry.operatingMode}`
                : '',
            hasValue(entry.scanningTime)
                ? `Scan time: ${entry.scanningTime} min`
                : '',
        ].filter(Boolean);

        return {
            primary: [amount, entry.condition].filter(Boolean).join(' — '),
            secondary: secondaryParts.length
                ? secondaryParts.join(' · ')
                : undefined,
        };
    });
}

function temperatureLines(conditionals: any): EntryLine[] | null {
    const entries = conditionals?.[SETTING_KEYS.temp];
    if (!Array.isArray(entries)) return null;
    const valid = entries.filter(
        (entry: any) => hasValue(entry?.maxRise) || hasValue(entry?.condition)
    );
    if (!valid.length) return null;

    return valid.map((entry: any) => ({
        primary: [
            hasValue(entry.maxRise) ? `≤ ${entry.maxRise}°C` : '',
            entry.condition,
        ]
            .filter(Boolean)
            .join(' — '),
    }));
}

function artefactLines(conditionals: any): EntryLine[] | null {
    const entries = conditionals?.[SETTING_KEYS.artefacts];
    if (!Array.isArray(entries)) return null;
    const valid = entries.filter(
        (entry: any) =>
            hasValue(entry?.distance) ||
            hasValue(entry?.condition) ||
            hasValue(entry?.pulseSequence) ||
            hasValue(entry?.mrSystem)
    );
    if (!valid.length) return null;

    return valid.map((entry: any) => {
        const secondaryParts = [
            hasValue(entry.pulseSequence)
                ? `Pulse sequence: ${entry.pulseSequence}`
                : '',
            hasValue(entry.mrSystem) ? `MR system: ${entry.mrSystem}T` : '',
        ].filter(Boolean);

        return {
            primary: [
                hasValue(entry.distance) ? `Distance: ${entry.distance} mm` : '',
                entry.condition,
            ]
                .filter(Boolean)
                .join(' — '),
            secondary: secondaryParts.length
                ? secondaryParts.join(' · ')
                : undefined,
        };
    });
}

function staticFieldLines(conditionals: any): EntryLine[] | null {
    const values = conditionals?.[SETTING_KEYS.fieldStrength];
    if (!Array.isArray(values)) return null;
    const valid = values.filter(hasValue);
    if (!valid.length) return null;

    const sorted = [...valid].sort((a, b) => Number(a) - Number(b));
    return [{ primary: sorted.map((value) => `${value} T`).join(', ') }];
}

const TEXT_BUILDERS: Record<string, (conditionals: any) => EntryLine[] | null> =
    {
        'Static Magnetic Field': staticFieldLines,
        'Spatial Gradient': spatialGradientLines,
        SAR: sarLines,
        'Temperature Rise': temperatureLines,
        Artefacts: artefactLines,
    };

export function formatConditionalsListText(
    label: string,
    conditionals: any,
    _t: TFunc
): string | null {
    const builder = TEXT_BUILDERS[label];
    if (!builder || !conditionals) return null;
    const lines = builder(conditionals);
    if (!lines) return null;

    return lines
        .flatMap((line) =>
            line.secondary ? [line.primary, `    ${line.secondary}`] : [line.primary]
        )
        .join('\n');
}
